"use client";

import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const steps = [
  { num: 1, text: "Processo de seleção" },
  { num: 2, text: "4 meses de capacitação em Java e IA" },
  { num: 3, text: "2 meses de projetos supervisionados por mentores" },
  {
    num: 4,
    text: "Hackathon de seleção para a 2ª etapa + 3 meses de atuação dentro das squads e mentoria com profissionais especializados",
  },
];

function Arrows() {
  return (
    <div className="flex flex-col items-center justify-center gap-1.5 py-4 w-12 md:w-16">
      {[0, 1, 2].map((i) => (
        <svg key={i} width="24" height="14" viewBox="0 0 24 14" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M2 2L12 10L22 2" stroke="#77df40" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ))}
    </div>
  );
}

export default function Timeline() {
  const containerRef = useRef<HTMLElement>(null);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start center", "end center"],
  });

  const lineHeight = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  return (
    <section id="etapas" className="bg-blue-medium py-32 relative overflow-hidden" ref={containerRef}>
      <div className="absolute inset-0 z-0 pointer-events-none opacity-95">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src="/images/formacao-devs-ia/Pixels.png" alt="" className="w-full h-full object-cover" />
      </div>

      <div className="container-custom relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          {/* Foto desktop */}
          <div className="hidden lg:flex relative w-full justify-end mt-20 lg:mt-0">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative z-10 rounded-[32px] overflow-hidden shadow-2xl w-full max-w-[700px] h-[600px] lg:h-[800px]"
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src="/images/formacao-devs-ia/Rapaz-Timeline.png" alt="Estudante Agibank" className="w-full h-full object-cover" />
              <div className="absolute top-0 right-0 w-full h-[30%] bg-gradient-to-b from-blue-dark/60 to-transparent" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1, delay: 0.5 }}
              className="absolute top-4 right-4 lg:-right-4 text-white/90 font-mono text-[10px] sm:text-xs md:text-sm whitespace-pre select-none pointer-events-none z-20 drop-shadow-lg animate-float-smooth"
            >
              {`model = Agibank.NeuralNetwork()

model.train(
    dataset="seu_talento",
    focus="Artificial_Intelligence",
    duration="9_months"
)
# Accuracy: 100%
# Status: Ready to build the future`}
            </motion.div>
          </div>

          {/* Timeline */}
          <div className="relative py-4 flex flex-col">
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.8 }}
              className="text-4xl md:text-5xl lg:text-[56px] font-bold text-white mb-16 text-left"
            >
              Etapas do Programa
            </motion.h2>

            <div className="relative">
              <div className="absolute top-0 bottom-0 left-[22px] md:left-[30px] w-1 bg-gradient-to-b from-transparent via-blue-main to-transparent opacity-40 rounded-full z-0" />
              <motion.div
                style={{ height: lineHeight }}
                className="absolute top-0 left-[22px] md:left-[30px] w-1 bg-secondary-main rounded-full origin-top z-10 shadow-[0_0_10px_rgba(119,223,64,0.8)]"
              />

              <div className="flex flex-col relative z-20">
                {steps.map((step, index) => (
                  <div key={index} className="flex flex-col">
                    <div className="flex items-center gap-6 md:gap-10">
                      <motion.div
                        initial={{ scale: 0, opacity: 0 }}
                        whileInView={{ scale: 1, opacity: 1 }}
                        viewport={{ once: true, margin: "0px" }}
                        transition={{ type: "spring", stiffness: 200, damping: 15 }}
                        className="relative w-12 h-12 md:w-16 md:h-16 shrink-0 flex items-center justify-center"
                      >
                        <div className="absolute inset-0 rounded-full border-[3px] border-secondary-main shadow-[0_0_15px_rgba(119,223,64,0.3)]" />
                        <div className="absolute inset-[6px] rounded-full bg-secondary-main" />
                        <span className="relative z-10 text-blue-dark text-xl md:text-2xl font-black">
                          {step.num}
                        </span>
                      </motion.div>

                      <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true, margin: "0px" }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                        className="flex-1"
                      >
                        <p className="text-white text-lg md:text-xl font-medium leading-snug">
                          {step.text}
                        </p>
                      </motion.div>
                    </div>

                    {index < steps.length - 1 && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true, margin: "0px" }}
                        transition={{ duration: 0.5, delay: 0.4 }}
                      >
                        <Arrows />
                      </motion.div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
